import {
  ButtonHeadless,
  InputLabel,
  Spin,
  TextInput,
} from '@wajeez-web/components/ui';
import { useEffect, useState } from 'react';
import {
  useForm,
  Controller,
  SubmitHandler,
  useFieldArray,
  UseControllerProps,
  FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useContentDispatch,
  CollectionComponetsActions,
  ContentEntityTypeActions,
  useContentSelector,
  FlowsActions,
  ComponentsActions,
} from '@wajeez-web/stores/content';
import { AiFillPlusCircle } from 'react-icons/ai';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { useParams, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useQuery } from '@wajeez-web/components/helpers';
import {
  AlertType,
  CommonActions,
  useCommonDispatch,
} from '@wajeez-web/stores/common';

import { FlowNavigationsForm } from '../components/FlowNavigationsForm';
import { BiLoaderAlt, BiTrashAlt } from 'react-icons/bi';
import { ConfirmationModal } from '../components/ConfirmationModal';
import {
  FormAutocompleteV2,
  FormInputController,
  FormModalSelectV2,
  FormSwitchInputV2,
  FormTextInputV2,
  WajeezFormInputV2,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { Accordion, Autocomplete, Select } from '@wajeez-web/components/mui';
import { ThunkStatus } from '@wajeez-web/stores/base';
import { UpdateFlowInput2 } from '@wajeez/wajeez.microservices/dist/api.Server.clients';
import { enumMapperValueAsNumber } from '@wajeez-web/core/utils';

type Section = { value: string; label: string };

type ContentEntityTypeInput = {
  id: string | number | undefined;
  value: {
    id: string | null | undefined;
    text: string | undefined;
  };
  label: string | undefined;
  isRequired: boolean;
  isError: boolean;
  isB2B?: boolean;
};

const paymentMethods = enumMapperValueAsNumber(Server.PaymentMethod2);

const sections: Section[] = [
  { value: 'general', label: 'General' },
  { value: 'navigations', label: 'Navigations' },
];

const tabResponsibilities = enumMapperValueAsNumber(Server.TabResponsibility);

export const CreateAndEditFlowV2Page = () => {
  const { id } = useParams<{ id: string }>();

  const query = useQuery();

  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();

  const [selectedSection, setSelectedSection] = useState<Section>(
    query.get('section')
      ? query.get('section') === 'navigations' && id
        ? { value: 'navigations', label: 'Navigations' }
        : { value: 'general', label: 'General' }
      : {
        value: 'general',
        label: 'General',
      }
  );
  const [validatePageInputs, setValidatePageInputs] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCreateFlowSuccess, setIsCreateFlowSuccess] = useState(false);

  const flowDetailsV2 = useContentSelector(
    ({ flows }) => flows.flowDetailsByIdV2
  );
  const flowDetailsDataV2 = flowDetailsV2.data;

  const contentEntityTypeLookupsStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.status
  );

  const contentEntityTypeNavigationsStatus = useContentSelector(
    ({ contentEntityType }) => contentEntityType.navigations.status
  );

  const createFlowV2Data = useContentSelector(
    ({ flows }) => flows.createFlowV2.data
  );

  const createFlowV2Status = useContentSelector(
    ({ flows }) => flows.createFlowV2.status
  );
  const editFlowV2Status = useContentSelector(
    ({ flows }) => flows.editFlowV2.status
  );

  useEffect(() => {
    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeLookupsAsync()
    );

    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeNavigationsAsync()
    );
  }, []);

  const onSubmit: SubmitHandler<Server.UpdateFlowInput2> = async (data) => {
    setValidatePageInputs(true);

    if (data.tabs?.length === 0) {
      return;
    }

    if (data.tabs) {
      for (const tab of data.tabs) {
        if (tab.pages?.length === 0) {
          return;
        }
      }
    }

    const mappedData = {
      ...data,
      paymentMethod: Number(data.paymentMethod),
      tabs: data.tabs?.map((t) => {
        return {
          ...t,
          tabResponsibility: Number(t.tabResponsibility),
          pages: t.pages?.map((p) => {
            return {
              ...p,
              componentId: p.componentId || undefined,
              collectionComponentId: p.collectionComponentId || undefined,
            };
          }),
        };
      }),
    };

    if (!id) {
      const res = await contentDispatch(
        FlowsActions.createFlowV2Async(mappedData)
      );

      if (res.meta.requestStatus === 'fulfilled') {
        setIsCreateFlowSuccess(true);
      }
    } else {
      contentDispatch(
        FlowsActions.editFlowV2Async({
          id: parseInt(id),
          ...mappedData,
        })
      );
    }
  };

  useEffect(() => {
    if (isCreateFlowSuccess && createFlowV2Data) {
      navigate(`/flows/v2/${createFlowV2Data}/edit?section=navigations`);

      setSelectedSection({ value: 'navigations', label: 'Navigations' });
    }
  }, [isCreateFlowSuccess, createFlowV2Data]);

  useEffect(() => {
    if (id) {
      contentDispatch(FlowsActions.getFlowDetailsByIdV2Async(parseInt(id)));
    }
    return () => {
      contentDispatch(FlowsActions.clear());
    };
  }, [id]);

  useEffect(() => {
    window.history.replaceState(
      null,
      '',
      `/flows/v2/${id || 'new'}${id ? '/edit' : ''}?section=${selectedSection.value
      }`
    );
  }, [selectedSection, id, createFlowV2Data]);

  const renderSections = () => {
    return sections.map((s) => {
      return (
        <div key={s.value}>
          <div
            className={`px-2 py-2 text-[14px] font-medium hover:bg-warmGray-100 hover:text-primary rounded-[4px] transition-colors cursor-pointer ${selectedSection.value === s.value
              ? 'bg-warmGray-100 text-primary'
              : 'text-warmGray-500'
              }`}
            onClick={() => {
              if (s.value === 'navigations' && !(createFlowV2Data || id)) {
                setIsConfirmationModalOpen(true);

                return;
              }

              setSelectedSection(s);
            }}
          >
            {s.label}
          </div>
        </div>
      );
    });
  };

  const renderSectionContent = () => {
    switch (selectedSection.value) {
      case 'general':
        return (
          <CreateAndEditFlowV2Form
            flowDetailsDataV2={flowDetailsDataV2 ? {
              ...flowDetailsDataV2,
              tabs: flowDetailsDataV2.tabs?.map((t) => {
                return {
                  ...t,
                  isB2B: t.type === Server.FlowTabType.B2B,
                  isCarPlay: t.type === Server.FlowTabType.CarPlay,
                };
              }),
            } : null}
            flowDetailsStatus={flowDetailsV2.status}
            onSubmit={onSubmit}
            savingStatus={
              createFlowV2Status === 'loading' || editFlowV2Status === 'loading'
            }
            validatePageInputs={validatePageInputs}
            setValidatePageInputs={setValidatePageInputs}
          />
        );
      case 'navigations':
        return <FlowNavigationsForm flowId={createFlowV2Data} version="v2" />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-inner-screen flex justify-start items-start">
      <div className="w-[250px] h-full px-4 py-6 border-e border-warmGray-200 overflow-x-hidden">
        <h1 className="w-full pb-2 border-b text-[22px] font-bold text-center">
          <ReturnTranslatedText
            text={
              !id ? `pageHeader.${'Create Flow'}` : `pageHeader.${'Edit Flow'}`
            }
            origintext={!id ? 'Create Flow' : 'Edit Flow'}
          />
        </h1>
        <div className="mt-3">{renderSections()}</div>
      </div>
      <div className="relative w-full h-inner-screen py-6 px-6 overflow-y-auto overflow-x-hidden">
        {flowDetailsV2.status === 'loading' ||
          contentEntityTypeLookupsStatus === 'loading' ||
          contentEntityTypeNavigationsStatus === 'loading' ? (
          <Spin />
        ) : (
          renderSectionContent()
        )}
      </div>

      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setIsConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            setIsConfirmationModalOpen(false);
          }}
          message="You should create the flow to continue!"
        />
      )}
    </div>
  );
};

type FlowComponent =
  | 'CollectionComponent'
  | 'CollectionComponentBody'
  | 'CollectionComponentLayout'
  | 'ComponentComponent'
  | 'ComponentPageBody';

export type CreateAndEditFlowV2FormProps = {
  flowDetailsDataV2: Server.DashboardFlowDetailsOutput2 | null;
  flowDetailsStatus: ThunkStatus;
  savingStatus: boolean;
  onSubmit: SubmitHandler<Server.UpdateFlowInput2>;
  validatePageInputs: boolean;
  setValidatePageInputs: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CreateAndEditFlowV2Form = ({
  flowDetailsDataV2,
  flowDetailsStatus,
  savingStatus,
  onSubmit,
  validatePageInputs,
  setValidatePageInputs,
}: CreateAndEditFlowV2FormProps) => {
  const contentDispatch = useContentDispatch();
  const commonDispatch = useCommonDispatch();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [openAccordionItemKey, setOpenAccordionItemKey] = useState<
    number | false
  >();
  const [activeAccordionItem, setActiveAccordionItem] = useState<{
    key: string;
    title: string | undefined;
  }>();

  const collectionComponentsLayout = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.collectionComponentLayoutLookups.data
  );

  const collectionComponentsBody = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.collectionComponentBodyLookups.data
  );

  const componentsComponent = useContentSelector(
    ({ components }) => components.componentComponentLookups.data
  );

  const componentsPageBody = useContentSelector(
    ({ components }) => components.componentPageBodyLookups.data
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    watch,
  } = useForm<Server.UpdateFlowInput2>({
    defaultValues: { id: undefined, ...flowDetailsDataV2 },
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const {
    fields: tabsFormArray,
    append: appendTab,
    remove: removeTab,
    update: updateTab,
  } = useFieldArray({
    control,
    name: 'tabs',
  });

  const { t } = useTranslation();

  const mapComponentsGeneralModalData = (
    flowComponentType: FlowComponent
  ): {
    value: string | undefined;
    label: string | undefined;
  }[] => {
    let componentsGeneral:
      | Server.CollectionComponentLookUpOutput[]
      | Server.ComponentsLookUpOutput[];

    switch (flowComponentType) {
      case 'CollectionComponentLayout':
        componentsGeneral = collectionComponentsLayout;

        break;
      case 'CollectionComponentBody':
        componentsGeneral = collectionComponentsBody;

        break;
      case 'ComponentComponent':
        componentsGeneral = componentsComponent;

        break;
      case 'ComponentPageBody':
        componentsGeneral = componentsPageBody;

        break;
      default:
        return [];
    }

    return componentsGeneral.map((cc) => {
      return {
        value: cc.id,
        label: cc.referenceName,
      };
    });
  };

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if (error) {
        setValidatePageInputs(true);
      }
    }
  }, [Object.values(errors)]);

  useEffect(() => {
    contentDispatch(
      CollectionComponetsActions.getCollectionComponentLayoutLookupsAsync({})
    );
    contentDispatch(
      CollectionComponetsActions.getCollectionComponentBodyLookupsAsync({})
    );

    contentDispatch(ComponentsActions.getComponentComponentLookupsAsync({}));
    contentDispatch(ComponentsActions.getComponentPageBodyLookupsAsync({}));
  }, []);

  const renderTabsInputs = () => {
    return (
      <Accordion
        data={tabsFormArray.map((t, i) => {
          return {
            key: t.id,
            title: (
              <div className="w-full flex justify-between items-center">
                <div className="flex justify-center items-center">
                  <div>
                    {activeAccordionItem?.key === t.id
                      ? activeAccordionItem.title || t.name
                      : t.name}
                  </div>
                  <div className="rtl:mr-1 ltr:ml-1">
                    {t.isB2B ? '(B2B)' : t.isCarPlay ? '(Car Play)' : '(B2C)'}
                  </div>
                </div>
                <div>
                  <DeleteIcon
                    onClick={() => {
                      removeTab(i);
                    }}
                  />
                </div>
              </div>
            ),
            body: (
              <div className="relative w-[650px] p-4 pb-6">
                <div className="w-full pb-8 grid grid-cols-2 gap-4">
                  <FormTextInputV2
                    control={control}
                    name={`tabs.${i}.name`}
                    fieldMeta={{
                      fieldType: Server.FieldType.Text,
                      required: true,
                      label: 'Name',
                      placeHolder: 'Name',
                      fieldOptions: {},
                    }}
                    onBlur={() => {
                      setActiveAccordionItem({
                        key: t.id,
                        title: getValues('tabs')?.[i].name,
                      });
                    }}
                  />
                  <FormInputController
                    control={control}
                    name={`tabs.${i}.tabResponsibility`}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {},
                    }}
                    renderInput={({ ...props }) => (
                      <Autocomplete
                        id={`tabs.${i}.tabResponsibility`}
                        options={tabResponsibilities.map((p) => {
                          return {
                            value: p.value,
                            label: p.label,
                          };
                        })}
                        label="Tab Responsibility"
                        color="#1f7983"
                        {...props}
                      />
                    )}
                  />
                  <div className="col-span-1">
                    <WajeezFormInputV2
                      control={control}
                      name={`tabs.${i}.iconReference`}
                      fieldMeta={{
                        fieldType: Server.FieldType.UploadImage,
                        fieldOptions: {},
                        label: 'Icon Reference',
                        placeHolder: 'Icon Reference',
                        required: true,
                      }}
                      id={`tabs.${i}.iconReference`}
                    />
                  </div>
                  <div className="col-span-1">
                    <WajeezFormInputV2
                      control={control}
                      name={`tabs.${i}.activeIconReference`}
                      fieldMeta={{
                        fieldType: Server.FieldType.UploadImage,
                        fieldOptions: {},
                        label: 'Active Icon Reference',
                        placeHolder: 'Active Icon Reference',
                        required: false,
                      }}
                      id={`tabs.${i}.activeIconReference`}
                    />
                  </div>
                  <div>
                    <FormModalSelectV2
                      name={`tabs.${i}.layoutId`}
                      control={control}
                      fieldMeta={{
                        fieldType: Server.FieldType.Select,
                        fieldOptions: {
                          defaultOptions: mapComponentsGeneralModalData(
                            'CollectionComponentLayout'
                          ),
                          selectOptions: mapComponentsGeneralModalData(
                            'CollectionComponentLayout'
                          ),
                        },
                        label: 'Layout',
                        required: true,
                      }}
                      onRender={() => {
                        contentDispatch(
                          CollectionComponetsActions.getCollectionComponentLayoutLookupsAsync(
                            {}
                          )
                        );
                      }}
                      onSearch={(searchText) => {
                        contentDispatch(
                          CollectionComponetsActions.getCollectionComponentLayoutLookupsAsync(
                            { searchText }
                          )
                        );
                      }}
                      required
                    />
                  </div>
                </div>
                <TabPages
                  control={control}
                  tabIndex={i}
                  name={`tabs.${i}`}
                  validatePageInputs={validatePageInputs}
                />
              </div>
            ),
          };
        })}
        openLastItemOnAdd={true}
        openItemIndex={openAccordionItemKey}
        onItemOpen={(itemIndex) => {
          setOpenAccordionItemKey(itemIndex);
        }}
      />
    );
  };

  const renderSectionContent = () => {
    return (
      <div className="w-full flex flex-col items-start justify-start">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full pb-6"
          noValidate
        >
          <div className="w-full pb-6 border-b-[2px]">
            <FormSectionTitle sectionNumber={1} title="Basic Information" />
            <div className="max-w-[300px]">
              <div className="relative w-full">
                <Controller
                  name="flowName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Name"
                      required
                      onChange={(name, value, event) => {
                        field.onChange(event);
                      }}
                      isControlled={true}
                    />
                  )}
                />
                {errors.flowName && (
                  <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
                    <ReturnTranslatedText
                      origintext="This field is required"
                      text={`general.${'This field is required'}`}
                    />
                  </div>
                )}
              </div>
              <div className="mt-3 w-full">
                <FormInputController
                  control={control}
                  name={`paymentMethod`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                    required: true,
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`paymentMethod`}
                      options={paymentMethods.map((p) => {
                        return {
                          value: p.value,
                          label: p.label,
                        };
                      })}
                      label="Payment Method"
                      color="#1f7983"
                      required
                      {...props}
                    />
                  )}
                />
              </div>
              <div className="mt-3 w-full">
                <FormModalSelectV2
                  name="searchAllComponentId"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {
                      defaultOptions: componentsComponent.map((p) => {
                        return {
                          value: p.id ?? '',
                          label: p.referenceName,
                        };
                      }),
                      selectOptions: componentsComponent.map((p) => {
                        return {
                          value: p.id ?? '',
                          label: p.referenceName,
                        };
                      }),
                    },
                    label: 'Search All Component',
                    required: true,
                  }}
                  onRender={() => {
                    contentDispatch(
                      ComponentsActions.getComponentComponentLookupsAsync({})
                    );
                  }}
                  onSearch={(searchText) => {
                    contentDispatch(
                      ComponentsActions.getComponentComponentLookupsAsync({
                        searchText,
                      })
                    );
                  }}
                  required
                />
              </div>
            </div>
          </div>
          <div className="mt-6">
            <FormSectionTitle sectionNumber={2} title="Tabs" />
            {tabsFormArray.length ? (
              <div className="mt-6 w-full flex flex-col justify-start items-start space-y-4">
                {renderTabsInputs()}
              </div>
            ) : null}
            <div className="relative mt-3 w-[200px]">
              <InputLabel
                text={t(`inputs.labels.${'Add Tab'}`, 'Add Tab') as string}
                name={`add-tab`}
                required={true}
              />
              <div className="h-[34px]">
                <Select
                  name={`add-tab`}
                  autoWidth={false}
                  isControlled
                  option={{ value: '', label: '' }}
                  options={[
                    {
                      value: 'b2B',
                      label: 'B2B',
                    },
                    { value: 'b2C', label: 'B2C' },
                    { value: 'carPlay', label: 'Car Play' },
                  ]}
                  onChange={(option) => {
                    if (option.value === 'b2B') {
                      const b2BTabsLength = tabsFormArray.filter(
                        (t) => t.isB2B
                      ).length;

                      if (b2BTabsLength < 5) {
                        appendTab({
                          name: undefined,
                          activeIconReference: undefined,
                          iconReference: undefined,
                          isB2B: true,
                          isCarPlay: false,
                          tabResponsibility: undefined,
                          layoutId: undefined,
                          pages: [],
                        }, {
                          shouldFocus: true
                        });
                      } else {
                        commonDispatch(
                          CommonActions.setAlert({
                            for: 'page',
                            show: true,
                            type: AlertType.WARNING,
                            messages: [
                              {
                                code: 0,
                                field: null,
                                message: t(
                                  `general.${"You can't add more than 5 B2B tabs"}`,
                                  "You can't add more than 5 B2B tabs"
                                ),
                              },
                            ],
                          })
                        );
                      }
                    } else if (option.value === 'b2C') {
                      const b2CTabsLength = tabsFormArray.filter(
                        (t) => !t.isB2B && !t.isCarPlay
                      ).length;

                      if (b2CTabsLength < 5) {
                        appendTab({
                          name: undefined,
                          activeIconReference: undefined,
                          iconReference: undefined,
                          isB2B: false,
                          isCarPlay: false,
                          tabResponsibility: undefined,
                          layoutId: undefined,
                          pages: [],
                        }, {
                          shouldFocus: true
                        });
                      } else {
                        commonDispatch(
                          CommonActions.setAlert({
                            for: 'page',
                            show: true,
                            type: AlertType.WARNING,
                            messages: [
                              {
                                code: 0,
                                field: null,
                                message: t(
                                  `general.${"You can't add more than 5 B2C tabs"}`,
                                  "You can't add more than 5 B2C tabs"
                                ),
                              },
                            ],
                          })
                        );
                      }
                    } else if (option.value === 'carPlay') {
                      const carPlayTabsLength = tabsFormArray.filter(
                        (t) => t.isCarPlay
                      ).length;

                      if (carPlayTabsLength < 4) {
                        appendTab({
                          name: undefined,
                          iconReference: undefined,
                          activeIconReference: undefined,
                          isB2B: false,
                          isCarPlay: true,
                          tabResponsibility: undefined,
                          layoutId: undefined,
                          pages: [],
                        }, {
                          shouldFocus: true
                        });
                      } else {
                        commonDispatch(
                          CommonActions.setAlert({
                            for: 'page',
                            show: true,
                            type: AlertType.WARNING,
                            messages: [
                              {
                                code: 0,
                                field: null,
                                message: t(
                                  `general.${"You can't add more than 4 Car Play tabs"}`,
                                  "You can't add more than 4 Car Play tabs"
                                ),
                              },
                            ],
                          })
                        );
                      }
                    }
                  }}
                />
              </div>
              {validatePageInputs && tabsFormArray.length === 0 && (
                <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
                  <ReturnTranslatedText
                    origintext="This field is required"
                    text={`general.${'This field is required'}`}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="w-[70px] absolute top-6 rtl:left-6 ltr:right-6">
            <ButtonHeadless
              paddingYStyle="py-2"
              type="submit"
              isDisabled={savingStatus}
            >
              <div className="w-full flex justify-center items-center text-white">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {savingStatus && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </form>
      </div>
    );
  };

  return (
    <>
      <div className="relative w-full h-inner-screen py-6 px-6">
        {renderSectionContent()}
      </div>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setIsConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            setIsConfirmationModalOpen(false);
          }}
          message="You should create the flow to continue!"
        />
      )}
    </>
  );
};

interface AddButtonProps {
  type: 'Tab' | 'Page';
  isActive: boolean;
  onClick: () => void;
}

const AddButton = ({ type, isActive, onClick }: AddButtonProps) => {
  return (
    <div
      className={`w-fit pt-1 flex justify-center items-center font-bold text-[16px] ${isActive
        ? 'text-primary cursor-pointer border-b border-transparent hover:border-primary hover:border-b'
        : 'text-gray-300 cursor-default'
        }`}
      onClick={() => {
        if (isActive) {
          onClick();
        }
      }}
    >
      <div className="rtl:ml-1 ltr:mr-1 text-[20px]">
        <AiFillPlusCircle />
      </div>
      <ReturnTranslatedText
        origintext={`Add ${type}`}
        text={`buttons.${`Add ${type}`}`}
      />
    </div>
  );
};

interface TabPagesProps<FormFields extends FieldValues>
  extends UseControllerProps<FormFields> {
  tabIndex: number;
  validatePageInputs: boolean;
}

const TabPages = ({
  control,
  tabIndex,
  validatePageInputs,
}: TabPagesProps<UpdateFlowInput2>) => {
  const contentDispatch = useContentDispatch();

  const [addedTabPageType, setAddedTabPageType] = useState<
    'Component' | 'CollectionComponent'
  >();

  const collectionComponentsLayout = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.collectionComponentLayoutLookups.data
  );

  const collectionComponentsBody = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.collectionComponentBodyLookups.data
  );

  const componentsComponent = useContentSelector(
    ({ components }) => components.componentComponentLookups.data
  );

  const componentsPageBody = useContentSelector(
    ({ components }) => components.componentPageBodyLookups.data
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: `tabs.${tabIndex}.pages`,
  });

  const { t } = useTranslation();

  const mapComponentsGeneralModalData = (
    flowComponentType: FlowComponent
  ): {
    value: string | undefined;
    label: string | undefined;
  }[] => {
    let componentsGeneral:
      | Server.CollectionComponentLookUpOutput[]
      | Server.ComponentsLookUpOutput[];

    switch (flowComponentType) {
      case 'CollectionComponentLayout':
        componentsGeneral = collectionComponentsLayout;

        break;
      case 'CollectionComponentBody':
        componentsGeneral = collectionComponentsBody;

        break;
      case 'ComponentComponent':
        componentsGeneral = componentsComponent;

        break;
      case 'ComponentPageBody':
        componentsGeneral = componentsPageBody;

        break;
      default:
        return [];
    }

    return componentsGeneral.map((cc) => {
      return {
        value: cc.id,
        label: cc.referenceName,
      };
    });
  };

  return (
    <div className="mt-0">
      {fields.length ? (
        <div className="mb-3 w-full flex flex-col justify-start items-start space-y-6">
          <InputLabel text={'Pages'} name={''} required={true} />
          {fields.map((p, ii) => {
            return (
              <div
                key={(p.name || '') + p.collectionComponentId + p.componentId}
                className="w-full px-4 pt-2 pb-4 flex justify-between items-center rounded shadow"
              >
                <div className="w-[300px] flex flex-col justify-start items-start space-y-3">
                  <FormTextInputV2
                    control={control}
                    key={`tabs.${tabIndex}.pages.${ii}.name`}
                    name={`tabs.${tabIndex}.pages.${ii}.name`}
                    fieldMeta={{
                      fieldType: Server.FieldType.Text,
                      required: true,
                      label: 'Name',
                      placeHolder: 'Name',
                      fieldOptions: {},
                    }}
                  />
                  {p.collectionComponentId ||
                    (!p.componentId && addedTabPageType === 'CollectionComponent') ? (
                    <FormModalSelectV2
                      key={`tabs.${tabIndex}.pages.${ii}.collectionComponentId`}
                      name={`tabs.${tabIndex}.pages.${ii}.collectionComponentId`}
                      control={control}
                      fieldMeta={{
                        fieldType: Server.FieldType.Select,
                        fieldOptions: {
                          defaultOptions: mapComponentsGeneralModalData(
                            'CollectionComponentBody'
                          ),
                          selectOptions: mapComponentsGeneralModalData(
                            'CollectionComponentBody'
                          ),
                        },
                        label: 'Collection Component',
                        required: true,
                      }}
                      onRender={() => {
                        contentDispatch(
                          CollectionComponetsActions.getCollectionComponentBodyLookupsAsync(
                            {}
                          )
                        );
                      }}
                      onSearch={(searchText) => {
                        contentDispatch(
                          CollectionComponetsActions.getCollectionComponentBodyLookupsAsync(
                            { searchText }
                          )
                        );
                      }}
                      required
                    />
                  ) : (
                    (p.componentId || (!p.collectionComponentId && addedTabPageType === 'Component')) && (
                      <FormModalSelectV2
                        key={`tabs.${tabIndex}.pages.${ii}.componentId`}
                        name={`tabs.${tabIndex}.pages.${ii}.componentId`}
                        control={control}
                        fieldMeta={{
                          fieldType: Server.FieldType.Select,
                          fieldOptions: {
                            defaultOptions:
                              mapComponentsGeneralModalData(
                                'ComponentPageBody'
                              ),
                            selectOptions:
                              mapComponentsGeneralModalData(
                                'ComponentPageBody'
                              ),
                          },
                          label: 'Component',
                          required: true,
                        }}
                        onRender={() => {
                          contentDispatch(
                            ComponentsActions.getComponentPageBodyLookupsAsync(
                              {}
                            )
                          );
                        }}
                        onSearch={(searchText) => {
                          contentDispatch(
                            ComponentsActions.getComponentPageBodyLookupsAsync({
                              searchText,
                            })
                          );
                        }}
                        required
                      />
                    )
                  )}
                  <WajeezFormInputV2
                    control={control}
                    name={`tabs.${tabIndex}.pages.${ii}.iconReference`}
                    fieldMeta={{
                      fieldType: Server.FieldType.UploadImage,
                      fieldOptions: {},
                      label: 'Icon Reference',
                      placeHolder: 'Icon Reference',
                    }}
                    id={`tabs.${tabIndex}.iconReference`}
                  />
                </div>
                <DeleteIcon
                  onClick={() => {
                    remove(ii);
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="relative w-[200px]">
        <InputLabel
          text={t(`inputs.labels.${'Add Page'}`, 'Add Page') as string}
          name={`add-page-${tabIndex}`}
          required={true}
        />
        <div className="h-[34px]">
          <Select
            name={`add-page-${tabIndex}`}
            autoWidth={false}
            option={{ value: '', label: '' }}
            options={[
              {
                value: 'collectionComponentId',
                label: 'Collection Component',
              },
              { value: 'componentId', label: 'Component' },
            ]}
            onChange={(option) => {
              if (option.value === 'collectionComponentId') {
                setAddedTabPageType('CollectionComponent');
              } else if (option.value === 'componentId') {
                setAddedTabPageType('Component');
              }

              append({
                name: '',
                iconReference: undefined,
                collectionComponentId: '',
                componentId: '',
              });
            }}
          />
        </div>
        {validatePageInputs && fields.length === 0 && (
          <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
            <ReturnTranslatedText
              origintext="This field is required"
              text={`general.${'This field is required'}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface DeleteIconProps {
  onClick: () => void;
}

const DeleteIcon = ({ onClick }: DeleteIconProps) => {
  return (
    <div
      className="text-[19px] text-red-500 cursor-pointer hover:opacity-75 active:opacity-100"
      onClick={onClick}
    >
      <BiTrashAlt />
    </div>
  );
};

interface FormSectionTitleProps {
  sectionNumber: number;
  title: string;
}

const FormSectionTitle = ({ sectionNumber, title }: FormSectionTitleProps) => {
  return (
    <div className="mb-4 w-full flex justify-start items-center">
      <div className="w-5 h-5 flex justify-center items-center bg-[#212529] rounded-full text-white text-[15px] font-medium">
        {sectionNumber}
      </div>
      <div className="rtl:mr-2 ltr:ml-2 text-[19px] font-medium">
        <ReturnTranslatedText text={`general.${title}`} origintext={title} />
      </div>
    </div>
  );
};
